import { useState, useCallback, useEffect } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import type { ThreadMessageLike } from '@assistant-ui/react';
import { MessageSquare } from 'lucide-react';
import AgentSelector from './AgentSelector';
import SandboxPanel from './SandboxPanel';
import { UIEventProvider, useUIEvent } from '../../contexts/UIEventContext';
import { ThoughtsProvider } from '../../contexts/ThoughtsContext';
import { CanvasProvider } from '../../contexts/CanvasContext';
import { useAgentRuntime } from '../../hooks/useAgentRuntime';
import { useAgentRunEvents } from '../../hooks/useAgentRunEvents';
import { useConversationRestore, type CanvasHint } from '../../hooks/useConversationRestore';
import { useResumeInFlightRun } from '../../hooks/useResumeInFlightRun';
import { ConversationThread } from '../../components/assistant-ui/conversation-thread';
import { useAuth } from '../../store/auth-context';
import { useAgent } from '../../hooks/useAgents';
import { useToolNameMap, ToolNameProvider } from '../../hooks/useTools';
import { useSpaceId } from '../../contexts/SpaceContext';
import { routes } from '../../lib/routes';

interface SandboxPageProps {
  visible?: boolean;
}

export default function SandboxPage({ visible = true }: SandboxPageProps) {
  const navigate = useNavigate();
  const spaceId = useSpaceId();
  // Sandbox lives under /c/s/:spaceId/sandbox/:agentId
  const match = useMatch('/c/s/:spaceId/sandbox/:agentId');
  const urlAgentId = match?.params.agentId ?? null;

  const { user, updatePreferences } = useAuth();
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(urlAgentId);
  const [conversationKey, setConversationKey] = useState(0);
  const [prevUrlAgentId, setPrevUrlAgentId] = useState<string | null>(urlAgentId);

  // Sync URL → state synchronously during render (no stale-render gap)
  if (urlAgentId !== prevUrlAgentId) {
    setPrevUrlAgentId(urlAgentId);
    if (urlAgentId && urlAgentId !== selectedAgentId) {
      setSelectedAgentId(urlAgentId);
      setConversationKey((k) => k + 1);
    }
  }

  // Compute saved conversationId: only use it if the saved agent matches the selected agent
  const savedPrefs = user?.preferences;
  const savedConversationId =
    savedPrefs?.sandboxAgentId === selectedAgentId
      ? (savedPrefs?.sandboxConversationId ?? undefined)
      : undefined;

  const handleSelect = useCallback(
    (agentId: string) => {
      setSelectedAgentId(agentId);
      setConversationKey((k) => k + 1);
      navigate(routes.console.spaceSandboxFor(spaceId, agentId), { replace: true });
    },
    [navigate],
  );

  const handleConversationChange = useCallback(
    (conversationId: string) => {
      if (selectedAgentId) {
        updatePreferences({
          sandboxConversationId: conversationId,
          sandboxAgentId: selectedAgentId,
        });
      }
    },
    [selectedAgentId, updatePreferences],
  );

  const handleReset = useCallback(async () => {
    try {
      await updatePreferences({
        sandboxConversationId: null,
        sandboxAgentId: null,
      });
    } catch {
      // Best-effort
    }
    setConversationKey((k) => k + 1);
  }, [updatePreferences]);

  return (
    <UIEventProvider spaceId={spaceId}>
      <ThoughtsProvider>
        {/* Runtime keeper — always mounted when agent selected, preserves conversation */}
        {selectedAgentId && (
          <SandboxConversation
            key={conversationKey}
            agentId={selectedAgentId}
            visible={visible}
            selectedAgentId={selectedAgentId}
            onSelect={handleSelect}
            onReset={handleReset}
            initialConversationId={savedConversationId}
            onConversationChange={handleConversationChange}
          />
        )}

        {/* Empty state — shown when visible and no agent selected */}
        {visible && !selectedAgentId && (
          <div className="flex h-screen">
            <AgentSelector
              selectedAgentId={null}
              onSelect={handleSelect}
              onReset={handleReset}
            />

            <div className="flex flex-1 flex-col">
              <div className="flex flex-1 items-center justify-center">
                <div className="text-center">
                  <MessageSquare className="mx-auto h-12 w-12 text-muted-foreground" />
                  <h3 className="mt-4 text-lg font-medium text-foreground">
                    Select an agent
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Choose an agent from the sidebar to start a conversation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </ThoughtsProvider>
    </UIEventProvider>
  );
}

interface SandboxConversationProps {
  agentId: string;
  visible: boolean;
  selectedAgentId: string;
  onSelect: (id: string) => void;
  onReset: () => void;
  initialConversationId?: string;
  onConversationChange: (conversationId: string) => void;
}

/**
 * Fetches saved conversation messages, then delegates to SandboxThread
 * once data is ready. This split ensures useAgentRuntime is only called
 * after initialMessages are available (useLocalRuntime only reads
 * initialMessages on first render).
 */
function SandboxConversation({
  agentId,
  visible,
  selectedAgentId,
  onSelect,
  onReset,
  initialConversationId,
  onConversationChange,
}: SandboxConversationProps) {
  const { dispatchUIEvent } = useUIEvent();
  const sandboxSpaceId = useSpaceId();
  const { messages: initialMessages, canvasHints, defaultCanvas, loading, inFlightRun } = useConversationRestore({
    conversationId: initialConversationId,
    spaceId: sandboxSpaceId,
    dispatchUIEvent,
    fetchRunDetails: true,
  });

  // Mount SandboxThread one frame after the target agent changes. Mounting
  // the assistant-ui runtime + Lexical composer synchronously inside the
  // selection click's commit crashes @assistant-ui/tap under StrictMode
  // ("Version is less than committed version" via TriggerPopover) and
  // unmounts the whole app; on agent→agent switch this also gives the old
  // thread a clean unmount commit before the new one mounts. Deliberately
  // independent of `visible` — the thread stays mounted while hidden to keep
  // in-flight runs alive.
  const [mountedForAgentId, setMountedForAgentId] = useState<string | null>(null);
  useEffect(() => {
    if (mountedForAgentId === agentId) return;
    const r = requestAnimationFrame(() => setMountedForAgentId(agentId));
    return () => cancelAnimationFrame(r);
  }, [agentId, mountedForAgentId]);

  if (loading || mountedForAgentId !== agentId) {
    if (!visible) return null;
    return (
      <div className="flex h-screen">
        <AgentSelector
          selectedAgentId={selectedAgentId}
          onSelect={onSelect}
          onReset={onReset}
        />
        <div className="flex flex-1 items-center justify-center">
          {loading && (
            <div className="text-sm text-muted-foreground">Loading conversation...</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <SandboxThread
      agentId={agentId}
      visible={visible}
      selectedAgentId={selectedAgentId}
      onSelect={onSelect}
      onReset={onReset}
      initialConversationId={initialConversationId}
      onConversationChange={onConversationChange}
      initialMessages={initialMessages}
      canvasHints={canvasHints}
      defaultCanvas={defaultCanvas}
      inFlightRun={inFlightRun}
    />
  );
}

/**
 * Holds the sandbox runtime alive. Only mounted after conversation fetch
 * completes so initialMessages are available from the first render.
 */
function SandboxThread({
  agentId,
  visible,
  selectedAgentId,
  onSelect,
  onReset,
  initialConversationId,
  onConversationChange,
  initialMessages,
  canvasHints,
  defaultCanvas,
  inFlightRun,
}: SandboxConversationProps & { initialMessages?: readonly ThreadMessageLike[]; canvasHints?: CanvasHint[]; defaultCanvas?: import('../../hooks/useConversationRestore').ConversationRestoreResult['defaultCanvas']; inFlightRun?: import('../../hooks/useConversationRestore').ConversationRestoreResult['inFlightRun'] }) {
  const { dispatchUIEvent } = useUIEvent();
  const sandboxSpaceId = useSpaceId();
  // Track the conversation id reactively so the background-run subscription is
  // scoped to THIS conversation server-side (undefined for a new chat, set once
  // the first message creates the conversation).
  const [liveConversationId, setLiveConversationId] = useState<string | undefined>(
    initialConversationId,
  );
  const handleConversationChange = useCallback(
    (id: string) => {
      setLiveConversationId(id);
      onConversationChange?.(id);
    },
    [onConversationChange],
  );
  const { runtime, conversationIdRef, activeRunIdStore, getToolName, consumeAttachmentsRef } = useAgentRuntime({
    agentId,
    spaceId: sandboxSpaceId,
    initialConversationId,
    onConversationChange: handleConversationChange,
    initialMessages,
    dispatchUIEvent,
  });
  useAgentRunEvents(runtime, activeRunIdStore, conversationIdRef, dispatchUIEvent, getToolName, visible, liveConversationId);
  // Reloaded/returned mid-run: re-attach the still-executing run so its live
  // trace resumes instead of looking lost.
  useResumeInFlightRun({ runtime, inFlightRun, enabled: visible });

  const { data: agent } = useAgent(agentId);
  const toolNameMap = useToolNameMap(agent?.tools);

  if (!visible) return null;

  return (
    <CanvasProvider conversationId={conversationIdRef.current ?? initialConversationId} spaceId={sandboxSpaceId} canvasHints={canvasHints} initialCanvas={defaultCanvas}>
      <ToolNameProvider value={toolNameMap}>
        <div className="flex h-screen">
          <AgentSelector
            selectedAgentId={selectedAgentId}
            onSelect={onSelect}
            onReset={onReset}
          />

          <div className="flex min-w-0 flex-1 flex-col">
            <ConversationThread runtime={runtime} agentId={agentId} consumeAttachmentsRef={consumeAttachmentsRef} />
          </div>

          <SandboxPanel runtime={runtime} agentId={agentId} conversationIdRef={conversationIdRef} />
        </div>
      </ToolNameProvider>
    </CanvasProvider>
  );
}
